import type { ErrorFeedbackType } from '@shared/components/feedback-content/FeedbackContent.types';

import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  InitAction,
  UseAppInitializationReturn,
} from './useAppInitialization.types';

import { useDeepLinkNavigation } from '../use-deep-link-navigation/useDeepLinkNavigation';
import {
  getLoadingMessage,
  getModuleRoute,
  resolveInitAction,
} from './useAppInitialization.utils';

/**
 * Orchestrates sequential app initialization: modules → deep link → module selection → orgs → routing.
 *
 * Decision logic lives in the pure `resolveInitAction` function (utils).
 * This hook provides the reactive state and executes the returned action.
 */
export function useAppInitialization(): UseAppInitializationReturn {
  const navigate = useNavigate();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [errorType, setErrorType] = useState<ErrorFeedbackType | null>(null);
  const [orgSelectionFailed, setOrgSelectionFailed] = useState(false);

  const {
    modules,
    selectedModule,
    selectModule,
    isModulesLoading,
    isModulesError,
  } = useModules();
  const {
    organizations,
    selectedOrganization,
    selectOrganization,
    isOrganizationsLoading,
    isOrganizationsError,
  } = useOrganizations();

  const { processDeepLink, clearDeepLink, hasDeepLink } = useDeepLinkNavigation(
    {
      modules,
      selectedModule,
      organizations,
      selectedOrganization,
      isModulesLoading,
      isOrganizationsLoading,
    }
  );

  const isManageModule = useMemo(
    () => selectedModule?.name === 'Manage',
    [selectedModule]
  );

  useEffect(() => {
    if (hasInitialized) return;

    // Deep link org selection failed — fall back to the user's current org
    if (orgSelectionFailed) {
      clearDeepLink();

      if (selectedOrganization && selectedModule) {
        const fallbackPath = getModuleRoute(
          selectedModule,
          selectedOrganization
        );
        navigate(fallbackPath, { replace: true });
        setHasInitialized(true);

        // Defer flag: CobaltFlagMessage mounts after navigate settles
        setTimeout(() => {
          emitFlagMessage(
            'danger',
            'Something went wrong. Please try again.',
            5000,
            true,
            'Get help',
            navigateToHelp
          );
        }, 0);
      } else if (!isOrganizationsLoading) {
        setErrorType('internal-server-error');
        setHasInitialized(true);
      }
      // If still loading, wait for org data to arrive
      return;
    }

    const deepLinkResult = hasDeepLink ? processDeepLink() : null;
    // Skip no-deep-link results to fall through to normal flow
    const effectiveDeepLink =
      deepLinkResult?.status === 'no-deep-link' ? null : deepLinkResult;

    const action: InitAction = resolveInitAction({
      deepLinkResult: effectiveDeepLink,
      isModulesLoading,
      isModulesError,
      modules,
      selectedModule,
      isManageModule,
      isOrganizationsLoading,
      isOrganizationsError,
      organizations,
      selectedOrganization,
    });

    if (action.type === 'wait') return;

    if ('clearDeepLink' in action && action.clearDeepLink) {
      clearDeepLink();
    }

    switch (action.type) {
      case 'navigate':
        if (action.saveToSession) {
          sessionStorage.setItem('appInitialized', 'true');
        }
        navigate(action.path, { replace: true });
        setHasInitialized(true);
        break;

      case 'select-module':
        selectModule(action.moduleName);
        break;

      case 'select-org':
        selectOrganization(action.orgId).catch(() => {
          setOrgSelectionFailed(true);
        });
        break;

      case 'done':
        setHasInitialized(true);
        break;

      case 'error':
        clearDeepLink();
        setErrorType(action.errorType);
        setHasInitialized(true);
        break;
    }
  }, [
    hasInitialized,
    orgSelectionFailed,
    hasDeepLink,
    processDeepLink,
    clearDeepLink,
    isModulesLoading,
    isModulesError,
    modules,
    selectedModule,
    selectModule,
    isManageModule,
    isOrganizationsLoading,
    isOrganizationsError,
    organizations,
    selectedOrganization,
    selectOrganization,
    navigate,
  ]);

  const loadingMessage = useMemo(
    () =>
      getLoadingMessage({
        isModulesLoading,
        isManageModule,
        isOrganizationsLoading,
        selectedModule,
        selectedOrganization,
      }),
    [
      isModulesLoading,
      isManageModule,
      isOrganizationsLoading,
      selectedModule,
      selectedOrganization,
    ]
  );

  return { loadingMessage, errorType };
}
